import { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import { X, RotateCcw, Check } from "lucide-react";

// Produce a cropped JPEG blob at up to 1600px on the longest side
async function getCroppedBlob(imageSrc, pixelCrop) {
  const image = await new Promise((res, rej) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => res(img);
    img.onerror = rej;
    img.src = imageSrc;
  });
  const maxSide = 1600;
  const ratio = pixelCrop.width / pixelCrop.height;
  let w = pixelCrop.width;
  let h = pixelCrop.height;
  if (w > maxSide || h > maxSide) {
    if (w > h) {
      w = maxSide;
      h = Math.round(maxSide / ratio);
    } else {
      h = maxSide;
      w = Math.round(maxSide * ratio);
    }
  }
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    w,
    h,
  );
  return new Promise((resolve) =>
    canvas.toBlob((b) => resolve(b), "image/jpeg", 0.9),
  );
}

export default function ImageCropper({ src, aspect = 4 / 5, onCancel, onCropped }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [areaPx, setAreaPx] = useState(null);
  const [saving, setSaving] = useState(false);

  const onCropComplete = useCallback((_, areaPixels) => {
    setAreaPx(areaPixels);
  }, []);

  const save = async () => {
    if (!areaPx) return;
    setSaving(true);
    try {
      const blob = await getCroppedBlob(src, areaPx);
      const file = new File([blob], `cropped-${Date.now()}.jpg`, {
        type: "image/jpeg",
      });
      await onCropped(file);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="image-cropper"
      className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
    >
      <div className="bg-white w-full max-w-3xl">
        <div className="flex items-center justify-between border-b border-[var(--ark-border)] px-5 py-3">
          <div>
            <div className="font-display text-xl">Adjust photo</div>
            <div className="text-[0.68rem] uppercase tracking-[0.16em] text-[var(--ark-muted)]">
              Drag to reposition · scroll or slide to zoom
            </div>
          </div>
          <button
            type="button"
            onClick={onCancel}
            data-testid="cropper-cancel"
            className="p-2 hover:text-[var(--ark-accent)]"
            aria-label="Cancel"
          >
            <X size={18} />
          </button>
        </div>
        <div className="relative bg-[var(--ark-bone)]" style={{ height: 480 }}>
          <Cropper
            image={src}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            aspect={aspect}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onRotationChange={setRotation}
            onCropComplete={onCropComplete}
            restrictPosition={false}
            showGrid={true}
            objectFit="contain"
            style={{
              containerStyle: { background: "var(--ark-bone)" },
              cropAreaStyle: {
                border: "2px solid #c19a6b",
                color: "rgba(0,0,0,0.55)",
              },
            }}
          />
        </div>
        <div className="px-5 py-4 border-t border-[var(--ark-border)] space-y-3">
          <div className="flex items-center gap-3">
            <span className="text-[0.68rem] uppercase tracking-[0.16em] text-[var(--ark-muted)] w-16">
              Zoom
            </span>
            <input
              type="range"
              min={1}
              max={4}
              step={0.01}
              value={zoom}
              data-testid="cropper-zoom"
              onChange={(e) => setZoom(Number(e.target.value))}
              className="flex-1 accent-[var(--ark-accent)]"
            />
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[0.68rem] uppercase tracking-[0.16em] text-[var(--ark-muted)] w-16">
              Rotate
            </span>
            <input
              type="range"
              min={-180}
              max={180}
              step={1}
              value={rotation}
              data-testid="cropper-rotation"
              onChange={(e) => setRotation(Number(e.target.value))}
              className="flex-1 accent-[var(--ark-accent)]"
            />
            <button
              type="button"
              onClick={() => {
                setRotation(0);
                setZoom(1);
                setCrop({ x: 0, y: 0 });
              }}
              data-testid="cropper-reset"
              className="p-2 text-[var(--ark-muted)] hover:text-black"
              title="Reset"
            >
              <RotateCcw size={14} />
            </button>
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-[var(--ark-border)]">
          <button
            type="button"
            onClick={onCancel}
            className="text-[0.72rem] uppercase tracking-[0.18em] text-[var(--ark-muted)] hover:text-black"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            data-testid="cropper-save"
            className="ark-btn disabled:opacity-60"
          >
            <Check size={14} className="mr-2" />
            {saving ? "Saving…" : "Use this crop"}
          </button>
        </div>
      </div>
    </div>
  );
}
